import { getActionTypes } from '../../../../HelperMethods/UtilityFunctions';

const types = getActionTypes('prioritizationModelManagement')([
  'SET_LOADER',
  'SET_FORM_LOADER',
  'LOAD_MODELS',
  'UPSERT_MODEL',
  'DELETE_MODEL',
  'SET_FACTORS_LIST',
  'SET_EDIT_VALUES',
  'CLEAR_EDIT_VALUES',
  'SET_SEARCH_VALUE',
  'SET_STEPPER_ENABLED',
  'SET_IS_FORMULA_SAVED',
  'SET_IS_SCORE_MATRIX_EDITED',
  'SET_IS_CONFIRM_MODAL_OPEN',
  'SET_PRIORITY_RULES',
]);

// Action creators
export function loadModels(models) {
  return { type: types.LOAD_MODELS, models };
}

export function setLoader(loader) {
  return { type: types.SET_LOADER, loader };
}

export function setFormLoader(loader) {
  return { type: types.SET_FORM_LOADER, loader };
}

export function upsertModel(model) {
  return { type: types.UPSERT_MODEL, model };
}

export function deleteModel(model) {
  return { type: types.DELETE_MODEL, modelId: model.id };
}

export function setFactorsList(factors) {
  return { type: types.SET_FACTORS_LIST, factors };
}

export function setEditValues(editValues) {
  return { type: types.SET_EDIT_VALUES, editValues };
}

export function clearEditValues() {
  return { type: types.CLEAR_EDIT_VALUES };
}

export function setSearchValue(searchValue) {
  return { type: types.SET_SEARCH_VALUE, searchValue };
}

export function setStepperEnabled(enabled) {
  return { type: types.SET_STEPPER_ENABLED, enabled };
}

export function setIsFormulaSaved(saved) {
  return { type: types.SET_IS_FORMULA_SAVED, saved };
}

export function setIsScoreMatrixEdited(edited) {
  return { type: types.SET_IS_SCORE_MATRIX_EDITED, edited };
}

export function setIsConfirmModalOpen(open) {
  return { type: types.SET_IS_CONFIRM_MODAL_OPEN, open };
}

export function setPriorityRules(rules) {
  return { type: types.SET_PRIORITY_RULES, rules };
}

// Initial state - following same pattern as factors
const initialState = {
  isModelsPageLoading: true,
  isModelsFormLoading: false,
  models: [],
  factorsList: [],
  editValues: {
    name: '',
    description: '',
    Formula: '',
    factorValues: [],
    matrixRows: [],
    taskTypeId: null,
    stopId: null,
    assetId: null,
    score: null,
    factorValuesMap: {},
  },
  searchValue: '',
  isStepperEnabled: false,
  filteredModels: [],
  isFormulaSaved: false,
  isScoreMatrixEdited: false,
  isConfirmModalOpen: false,
  priorityRules: [], // 
};

// Reducer
export default function prioritizationModelReducer(state = initialState, action) {
  switch (action.type) {
    case types.SET_LOADER: {
      return { ...state, isModelsPageLoading: action.loader };
    }
    
    case types.SET_FORM_LOADER: {
      return { ...state, isModelsFormLoading: action.loader };
    }
    
    case types.LOAD_MODELS: {
      return { 
        ...state, 
        models: action.models,
        filteredModels: action.models
      };
    }
    
    case types.UPSERT_MODEL: {
      const { model } = action;
      const updatedModels = [
        ...state.models.filter(({ id: modelId }) => modelId !== model.id), 
        model
      ];
      return {
        ...state,
        models: updatedModels,
        filteredModels: state.searchValue 
          ? updatedModels.filter(m => 
              m.name?.toLowerCase().includes(state.searchValue.toLowerCase()) ||
              m.description?.toLowerCase().includes(state.searchValue.toLowerCase())
            )
          : updatedModels
      };
    }
    
    case types.DELETE_MODEL: {
      const updatedModels = state.models.filter(({ id: modelId }) => modelId !== action.modelId);
      return {
        ...state,
        models: updatedModels,
        filteredModels: state.searchValue 
          ? updatedModels.filter(m => 
              m.name?.toLowerCase().includes(state.searchValue.toLowerCase()) ||
              m.description?.toLowerCase().includes(state.searchValue.toLowerCase())
            )
          : updatedModels
      };
    }
    
    case types.SET_FACTORS_LIST: {
      return { ...state, factorsList: action.factors };
    }
    
    case types.SET_EDIT_VALUES: {
      return { ...state, editValues: { ...state.editValues, ...action.editValues } };
    }
    
    case types.CLEAR_EDIT_VALUES: {
      return { ...state, editValues: initialState.editValues };
    }
    
    case types.SET_SEARCH_VALUE: {
      const filteredModels = action.searchValue
        ? state.models.filter(model =>
            model.name?.toLowerCase().includes(action.searchValue.toLowerCase()) ||
            model.description?.toLowerCase().includes(action.searchValue.toLowerCase())
          )
        : state.models;
        
      return { 
        ...state, 
        searchValue: action.searchValue,
        filteredModels
      };
    }
    
    case types.SET_STEPPER_ENABLED: {
      return { ...state, isStepperEnabled: action.enabled };
    }
    
    case types.SET_IS_FORMULA_SAVED: {
      return { ...state, isFormulaSaved: action.saved };
    }
    
    case types.SET_IS_SCORE_MATRIX_EDITED: {
      return { ...state, isScoreMatrixEdited: action.edited };
    }
    
    case types.SET_IS_CONFIRM_MODAL_OPEN: {
      return { ...state, isConfirmModalOpen: action.open };
    }
    
    case types.SET_PRIORITY_RULES: {
      return { ...state, priorityRules: action.rules };
    }
    
    default:
      return state;
  }
}

// Selectors - following same pattern as factors
export const getModelsLoadingState = (state) => state.prioritizationModelReducer.isModelsPageLoading;
export const getModelsFormLoadingState = (state) => state.prioritizationModelReducer.isModelsFormLoading;
export const getAllModels = (state) => state.prioritizationModelReducer.models;
export const getFilteredModels = (state) => state.prioritizationModelReducer.filteredModels;
export const getFactorsList = (state) => state.prioritizationModelReducer.factorsList;
export const getEditValues = (state) => state.prioritizationModelReducer.editValues;
export const getSearchValue = (state) => state.prioritizationModelReducer.searchValue;
export const getStepperEnabled = (state) => state.prioritizationModelReducer.isStepperEnabled;
export const getIsFormulaSaved = (state) => state.prioritizationModelReducer.isFormulaSaved;
export const getIsScoreMatrixEdited = (state) => state.prioritizationModelReducer.isScoreMatrixEdited;
export const getIsConfirmModalOpen = (state) => state.prioritizationModelReducer.isConfirmModalOpen;
export const getPriorityRules = (state) => state.prioritizationModelReducer.priorityRules;
